import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  MoreHorizontal,
  ArrowLeftRight,
  BookOpen,
  Download,
  ExternalLink,
  FileCode2,
  FileJson,
  FileType2,
  GitPullRequest,
  History,
  Pencil,
  RotateCcw,
  Send,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ChangeRequestSummaryDto, DocumentDetailDto, Paginated, PdfThemeDto, VersionSummaryDto } from '@imsenta/shared';
import { PageHeader } from '@/components/AppShell';
import { DiffSummary, MarkdownDiff, SectionDiffList } from '@/components/DiffView';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  StatusBadge,
  Tabs,
  useToast,
} from '@/components/ui';
import { DocumentReadView } from '@/editor/DocumentEditor';
import { api, ApiError, downloadFile } from '@/lib/api';
import { fmtDate, fmtDateTime, fmtRelative } from '@/lib/format';
import { useSession } from '@/lib/session';

type Tab = 'content' | 'history' | 'compare' | 'markdown' | 'changes' | 'details';

export function DocumentPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const { can, session } = useSession();
  const [tab, setTab] = useState<Tab>('content');
  const [publishOpen, setPublishOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const manageRef = useRef<HTMLDivElement>(null);
  const [exportOpen, setExportOpen] = useState(false);

  const { data: doc, isLoading, error } = useQuery({
    queryKey: ['document', id],
    queryFn: () => api.get<DocumentDetailDto>(`/documents/${id}`),
  });

  const { data: changeRequests } = useQuery({
    queryKey: ['document-crs', id],
    queryFn: () => api.get<Paginated<ChangeRequestSummaryDto>>('/change-requests', { documentId: id, state: 'all', pageSize: 50 }),
    enabled: Boolean(doc),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['document', id] });
    queryClient.invalidateQueries({ queryKey: ['documents'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  useEffect(() => {
    if (!manageOpen) return;
    const onClick = (event: MouseEvent) => {
      if (!manageRef.current?.contains(event.target as Node)) setManageOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [manageOpen]);

  const archive = useMutation({
    mutationFn: (status: string) => api.post(`/documents/${id}/status`, { status }),
    onSuccess: () => {
      invalidate();
      setArchiveOpen(false);
      push('Document archived', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not archive', 'danger'),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/documents/${id}`),
    onSuccess: () => {
      invalidate();
      push('Document deleted', 'success');
      navigate('/documents');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not delete', 'danger'),
  });

  const restore = useMutation({
    mutationFn: (versionId: string) => api.post(`/documents/${id}/versions/${versionId}/restore`),
    onSuccess: () => {
      invalidate();
      push('Version restored into the working draft', 'success');
      setTab('content');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not restore', 'danger'),
  });

  if (isLoading) return <Spinner label="Loading document…" />;
  if (error || !doc) {
    return (
      <Card>
        <EmptyState
          title="Document not available"
          description={error instanceof ApiError ? error.message : 'It may have been deleted, or you may not have access.'}
          action={<Button onClick={() => navigate('/documents')}>Back to documents</Button>}
        />
      </Card>
    );
  }

  const displayVersion = doc.draft ?? doc.published;
  const openCrs = (changeRequests?.items ?? []).filter((c) => ['open', 'approved', 'changes_requested'].includes(c.state));

  return (
    <>
      <PageHeader
        back={{ to: '/documents', label: 'Documents' }}
        title={doc.title}
        description={doc.summary ?? undefined}
        meta={
          <>
            <Badge tone="neutral">{doc.reference}</Badge>
            <Badge tone="brand">{doc.documentTypeName}</Badge>
            <StatusBadge status={doc.status} />
            {doc.published ? <span className="text-[12.5px] text-ink-500">Published {doc.published.label} · {fmtDate(doc.published.publishedAt)}</span> : null}
            {doc.draft ? <Badge tone="warning">unpublished changes</Badge> : null}
            {doc.reviewDue ? <span className="text-[12.5px] text-ink-500">Review due {fmtDate(doc.reviewDue)}</span> : null}
          </>
        }
        actions={
          <>
            {doc.status === 'published' ? (
              <Button icon={<BookOpen className="size-4" />} onClick={() => navigate(`/catalog/${doc.reference}`)}>
                Read in catalog
              </Button>
            ) : null}
            <Button icon={<Download className="size-4" />} onClick={() => setExportOpen(true)}>
              Export
            </Button>
            {can('changeRequest:create') && doc.published ? (
              <Button
                icon={<GitPullRequest className="size-4" />}
                onClick={() => navigate(`/change-requests?new=1&documentId=${doc.id}`)}
              >
                Propose change
              </Button>
            ) : null}
            {can('document:edit') ? (
              <Button variant="primary" icon={<Pencil className="size-4" />} onClick={() => navigate(`/documents/${doc.id}/edit`)}>
                Edit
              </Button>
            ) : null}
            {can('document:publish') && doc.draft ? (
              <Button variant="success" icon={<Send className="size-4" />} onClick={() => setPublishOpen(true)}>
                Publish
              </Button>
            ) : null}
            {/*
              Archiving and deleting live here rather than in a panel on one
              tab. They were previously reachable only from the content tab,
              which is why nobody could find them.
            */}
            {can('document:archive') || can('document:delete') ? (
              <div className="relative" ref={manageRef}>
                <Button
                  icon={<MoreHorizontal className="size-4" />}
                  aria-label="More actions"
                  aria-expanded={manageOpen}
                  onClick={() => setManageOpen((v) => !v)}
                />
                {manageOpen ? (
                  <div className="pf-fade-in absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-xl border border-ink-200 bg-white py-1.5 shadow-xl">
                    {can('document:archive') && doc.status !== 'archived' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setManageOpen(false);
                          setArchiveOpen(true);
                        }}
                        className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-[13px] text-ink-700 hover:bg-ink-50"
                      >
                        <Archive className="size-4 text-ink-400" /> Archive document
                      </button>
                    ) : null}
                    {can('document:archive') && doc.status === 'archived' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setManageOpen(false);
                          archive.mutate(doc.published ? 'published' : 'draft');
                        }}
                        className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-[13px] text-ink-700 hover:bg-ink-50"
                      >
                        <Archive className="size-4 text-ink-400" /> Restore from archive
                      </button>
                    ) : null}
                    {can('document:delete') && !doc.published ? (
                      <button
                        type="button"
                        onClick={() => {
                          setManageOpen(false);
                          setDeleteOpen(true);
                        }}
                        className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-[13px] text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="size-4" /> Delete permanently
                      </button>
                    ) : null}
                    {can('document:delete') && doc.published ? (
                      /*
                        The server refuses this while a published version
                        exists. Saying why, next to the action that does work,
                        beats a hidden button or one that errors on click.
                      */
                      <p className="border-t border-ink-100 px-4 pt-2.5 pb-1 text-[12px] leading-relaxed text-ink-500">
                        Published documents cannot be deleted, because that would destroy the record of what was in
                        force. Archive it instead — or archive it first, then delete.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        }
      />

      {openCrs.length > 0 ? (
        <div className="mb-4">
          <Alert tone="info" title={`${openCrs.length} open change request${openCrs.length === 1 ? '' : 's'}`}>
            <ul className="mt-1 space-y-0.5">
              {openCrs.map((cr) => (
                <li key={cr.id}>
                  <Link to={`/change-requests/${cr.id}`} className="font-medium hover:underline">
                    #{cr.number} {cr.title}
                  </Link>{' '}
                  — {cr.approvals}/{cr.requiredApprovals} approvals
                </li>
              ))}
            </ul>
          </Alert>
        </div>
      ) : null}

      <Tabs
        className="mb-4"
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'content', label: 'Content' },
          { id: 'history', label: 'Version history', count: doc.versions.length },
          { id: 'compare', label: 'Compare versions' },
          { id: 'markdown', label: 'Markdown' },
          { id: 'changes', label: 'Change requests', count: changeRequests?.total ?? 0 },
          { id: 'details', label: 'Details' },
        ]}
      />

      {tab === 'content' ? (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_260px] [&>*]:min-w-0">
          <Card className="px-5 py-6 sm:px-8 sm:py-7">
            {displayVersion ? (
              <>
                {doc.draft ? (
                  <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-900">
                    You are viewing the unpublished draft. The published version is {doc.published?.label ?? 'not yet issued'}.
                  </div>
                ) : null}
                <h1 className="mb-6 border-b border-ink-200 pb-4 text-2xl font-semibold text-ink-900">{displayVersion.title}</h1>
                <DocumentReadView content={displayVersion.content} />
              </>
            ) : (
              <EmptyState title="No content yet" description="Open the editor to start writing." />
            )}
          </Card>

          <div className="space-y-4">
            <Card>
              <CardHeader title="At a glance" />
              <dl className="space-y-2.5 px-5 py-4 text-[13px]">
                <Row label="Owner">{doc.owner ? <span className="inline-flex items-center gap-1.5"><Avatar name={doc.owner.name} email={doc.owner.email} size={20} />{doc.owner.name}</span> : '—'}</Row>
                <Row label="Version">{doc.published?.label ?? 'Not published'}</Row>
                <Row label="Effective">{fmtDate(doc.published?.effectiveFrom)}</Row>
                <Row label="Review due">{fmtDate(doc.reviewDue)}</Row>
                <Row label="Classification">{String(doc.metadata.classification ?? '—')}</Row>
                <Row label="Words">{displayVersion?.wordCount ?? 0}</Row>
              </dl>
            </Card>

            {doc.outgoingReferences.length ? (
              <Card>
                <CardHeader title="References out" />
                <ul className="divide-y divide-ink-100">
                  {doc.outgoingReferences.map((ref, index) => (
                    <li key={index} className="px-5 py-2 text-[13px]">
                      {ref.documentId ? (
                        <Link to={`/documents/${ref.documentId}`} className="text-brand-700 hover:underline">
                          {ref.reference ? `${ref.reference} · ` : ''}
                          {ref.label}
                        </Link>
                      ) : (
                        <span className="text-ink-600">{ref.label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}

            {doc.incomingReferences.length ? (
              <Card>
                <CardHeader title="Referenced by" />
                <ul className="divide-y divide-ink-100">
                  {doc.incomingReferences.map((ref) => (
                    <li key={ref.documentId} className="px-5 py-2 text-[13px]">
                      <Link to={`/documents/${ref.documentId}`} className="text-brand-700 hover:underline">
                        {ref.reference} · {ref.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}

          </div>
        </div>
      ) : null}

      {tab === 'history' ? <HistoryTab doc={doc} onRestore={(v) => restore.mutate(v)} canEdit={can('document:edit')} /> : null}
      {tab === 'compare' ? <CompareTab documentId={doc.id} versions={doc.versions} /> : null}
      {tab === 'markdown' ? <MarkdownTab doc={doc} /> : null}
      {tab === 'changes' ? <ChangesTab items={changeRequests?.items ?? []} documentId={doc.id} canCreate={can('changeRequest:create')} /> : null}
      {tab === 'details' ? <DetailsTab doc={doc} /> : null}

      <PublishModal open={publishOpen} onClose={() => setPublishOpen(false)} doc={doc} onDone={invalidate} />
      <ExportModal open={exportOpen} onClose={() => setExportOpen(false)} doc={doc} />

      <ConfirmDialog
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        onConfirm={() => archive.mutate('archived')}
        title="Archive this document?"
        message="It will be hidden from the library and the catalog, but its history is kept and it can be restored."
        confirmLabel="Archive"
        tone="danger"
        loading={archive.isPending}
      />
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => remove.mutate()}
        title={`Delete ${doc.reference} permanently?`}
        /*
          Naming what goes, rather than "this cannot be undone". Deleting a
          published policy destroys the record of what was in force and when,
          which is the evidence an audit asks for — archiving keeps all of it
          and is reversible, so it is offered here as the way out.
        */
        message={
          <>
            <span className="block">
              This removes <span className="font-medium text-ink-800">{doc.title}</span>,{' '}
              {doc.versions.length === 1 ? 'its only version' : `all ${doc.versions.length} of its versions`}, its
              change requests and its comments.
            </span>
            {doc.incomingReferences.length ? (
              <span className="mt-2 block text-rose-700">
                {doc.incomingReferences.length} other document
                {doc.incomingReferences.length === 1 ? '' : 's'} cross-reference this one. Those references will be left
                pointing at nothing.
              </span>
            ) : null}
          </>
        }
        confirmLabel="Delete permanently"
        confirmPhrase={doc.reference}
        tone="danger"
        loading={remove.isPending}
      />
      {session ? null : null}
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-ink-500">{label}</dt>
      <dd className="text-right font-medium text-ink-800">{children}</dd>
    </div>
  );
}

function HistoryTab({ doc, onRestore, canEdit }: { doc: DocumentDetailDto; onRestore: (versionId: string) => void; canEdit: boolean }) {
  return (
    <Card>
      <CardHeader title="Version history" description="Published versions are immutable. Restoring copies a version into the working draft." />
      <ul className="divide-y divide-ink-100">
        {doc.versions.map((version) => (
          <li key={version.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3.5">
            <div className="w-20 shrink-0">
              <Badge tone={version.state === 'published' ? 'success' : version.state === 'draft' ? 'neutral' : 'info'}>{version.label}</Badge>
            </div>
            <div className="min-w-0 flex-1 basis-48">
              <p className="text-[13.5px] font-medium text-ink-800">{version.changeSummary || <span className="text-ink-400 italic">No summary recorded</span>}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-[12px] text-ink-500">
                {version.createdBy ? (
                  <>
                    <Avatar name={version.createdBy.name} email={version.createdBy.email} size={16} />
                    {version.createdBy.name} ·
                  </>
                ) : null}
                {version.publishedAt ? `published ${fmtDateTime(version.publishedAt)}` : `saved ${fmtRelative(version.createdAt)}`}
                {version.changeRequestId ? ' · via change request' : ''}
              </p>
            </div>
            <span className="text-[12px] text-ink-500">{version.wordCount} words</span>
            <StatusBadge status={version.state} />
            {canEdit && version.state !== 'draft' ? (
              <Button size="sm" icon={<RotateCcw className="size-3.5" />} onClick={() => onRestore(version.id)}>
                Restore
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function CompareTab({ documentId, versions }: { documentId: string; versions: VersionSummaryDto[] }) {
  const usable = versions;
  const [base, setBase] = useState(usable[1]?.id ?? usable[0]?.id ?? '');
  const [compare, setCompare] = useState(usable[0]?.id ?? '');
  const [showRaw, setShowRaw] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['diff', documentId, base, compare],
    queryFn: () => api.get<{ base: VersionSummaryDto; compare: VersionSummaryDto; diff: Parameters<typeof DiffSummary>[0]['diff']; lines: Parameters<typeof MarkdownDiff>[0]['hunks'] }>(
      `/documents/${documentId}/diff`,
      { base, compare },
    ),
    enabled: Boolean(base && compare && base !== compare),
  });

  if (usable.length < 2) {
    return (
      <Card>
        <EmptyState icon={<ArrowLeftRight className="size-8" />} title="Only one version so far" description="Publish a change to compare versions." />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Compare versions"
        actions={
          <Checkbox label="Raw Markdown" checked={showRaw} onChange={(e) => setShowRaw(e.target.checked)} />
        }
      />
      <div className="flex flex-wrap items-end gap-3 border-b border-ink-100 px-5 py-4">
        <Field label="From" className="min-w-48 flex-1">
          <Select value={base} onChange={(e) => setBase(e.target.value)}>
            {usable.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label} · {v.publishedAt ? fmtDate(v.publishedAt) : fmtDate(v.createdAt)}
              </option>
            ))}
          </Select>
        </Field>
        <ArrowLeftRight className="mb-2.5 size-4 text-ink-400" />
        <Field label="To" className="min-w-48 flex-1">
          <Select value={compare} onChange={(e) => setCompare(e.target.value)}>
            {usable.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label} · {v.publishedAt ? fmtDate(v.publishedAt) : fmtDate(v.createdAt)}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="px-5 py-4">
        {base === compare ? (
          <p className="py-6 text-center text-[13.5px] text-ink-500">Choose two different versions to compare.</p>
        ) : isLoading ? (
          <Spinner />
        ) : error ? (
          <Alert tone="danger">{error instanceof ApiError ? error.message : 'Could not build the comparison.'}</Alert>
        ) : data ? (
          <>
            <div className="mb-4">
              <DiffSummary diff={data.diff} />
            </div>
            {showRaw ? <MarkdownDiff hunks={data.lines} /> : <SectionDiffList diff={data.diff} />}
          </>
        ) : null}
      </div>
    </Card>
  );
}

function MarkdownTab({ doc }: { doc: DocumentDetailDto }) {
  const { push } = useToast();
  const version = doc.draft ?? doc.published;
  if (!version) return <Card><EmptyState title="No content to show" /></Card>;
  return (
    <Card>
      <CardHeader
        title="Stored Markdown"
        description="This is exactly what the document exports as — machine readable, with YAML front matter."
        actions={
          <>
            <Button
              size="sm"
              onClick={() => {
                void navigator.clipboard.writeText(version.markdown);
                push('Markdown copied to the clipboard', 'success');
              }}
            >
              Copy
            </Button>
            <Button size="sm" icon={<Download className="size-3.5" />} onClick={() => downloadFile(`/documents/${doc.id}/export`, { format: 'markdown', versionId: version.id })}>
              Download .md
            </Button>
          </>
        }
      />
      <pre className="max-h-[36rem] overflow-auto px-5 py-4 font-mono text-[12.5px] leading-relaxed whitespace-pre-wrap text-ink-700">
        {version.markdown}
      </pre>
    </Card>
  );
}

function ChangesTab({ items, documentId, canCreate }: { items: ChangeRequestSummaryDto[]; documentId: string; canCreate: boolean }) {
  const navigate = useNavigate();
  return (
    <Card>
      <CardHeader
        title="Change requests"
        actions={
          canCreate ? (
            <Button size="sm" variant="primary" onClick={() => navigate(`/change-requests?new=1&documentId=${documentId}`)}>
              Propose a change
            </Button>
          ) : null
        }
      />
      {items.length === 0 ? (
        <EmptyState icon={<GitPullRequest className="size-8" />} title="No change requests" description="Proposed edits to this document will appear here." />
      ) : (
        <ul className="divide-y divide-ink-100">
          {items.map((cr) => (
            <li key={cr.id}>
              <Link to={`/change-requests/${cr.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-3 hover:bg-ink-50">
                <Badge tone="neutral">#{cr.number}</Badge>
                <span className="min-w-0 flex-1 basis-40 truncate text-[13.5px] font-medium text-ink-800">{cr.title}</span>
                <span className="text-[12px] text-ink-500">
                  {cr.createdBy?.name ?? 'Unknown'} · {fmtRelative(cr.createdAt)}
                </span>
                <span className="text-[12px] text-ink-500">
                  {cr.approvals}/{cr.requiredApprovals}
                </span>
                <StatusBadge status={cr.state} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function DetailsTab({ doc }: { doc: DocumentDetailDto }) {
  const entries = Object.entries(doc.metadata).filter(([, v]) => v !== null && v !== undefined && v !== '');
  return (
    <div className="grid gap-5 lg:grid-cols-2 [&>*]:min-w-0">
      <Card>
        <CardHeader title="Metadata" description="Exported as YAML front matter with every Markdown copy." />
        <dl className="divide-y divide-ink-100">
          {entries.length === 0 ? (
            <p className="px-5 py-6 text-[13px] text-ink-500">No metadata recorded.</p>
          ) : (
            entries.map(([key, value]) => (
              <div key={key} className="flex items-start justify-between gap-4 px-5 py-2.5 text-[13px]">
                <dt className="font-mono text-ink-500">{key}</dt>
                <dd className="text-right font-medium text-ink-800">{Array.isArray(value) ? value.join(', ') : String(value)}</dd>
              </div>
            ))
          )}
        </dl>
      </Card>
      <Card>
        <CardHeader title="Record" />
        <dl className="divide-y divide-ink-100">
          {(
            [
              ['Reference', doc.reference],
              ['Slug', doc.slug],
              ['Type', doc.documentTypeName],
              ['Template', doc.template?.name ?? '—'],
              ['Created', fmtDateTime(doc.createdAt)],
              ['Last updated', fmtDateTime(doc.updatedAt)],
              ['Document id', doc.id],
            ] as [string, string][]
          ).map(([label, value]) => (
            <div key={label} className="flex items-start justify-between gap-4 px-5 py-2.5 text-[13px]">
              <dt className="text-ink-500">{label}</dt>
              <dd className="text-right font-medium break-all text-ink-800">{value}</dd>
            </div>
          ))}
        </dl>
      </Card>
    </div>
  );
}

function PublishModal({ open, onClose, doc, onDone }: { open: boolean; onClose: () => void; doc: DocumentDetailDto; onDone: () => void }) {
  const { push } = useToast();
  const [bump, setBump] = useState<'major' | 'minor'>(doc.published ? 'minor' : 'major');
  const [summary, setSummary] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [reviewDue, setReviewDue] = useState('');

  const publish = useMutation({
    mutationFn: () =>
      api.post(`/documents/${doc.id}/publish`, {
        versionBump: bump,
        changeSummary: summary || undefined,
        effectiveFrom: effectiveFrom || null,
        reviewDue: reviewDue || null,
      }),
    onSuccess: () => {
      onDone();
      onClose();
      push('Document published', 'success');
    },
    onError: (err) => push(err instanceof ApiError ? err.message : 'Could not publish', 'danger'),
  });

  const current = doc.published;
  const nextLabel = bump === 'major' ? `v${(current?.major ?? 0) + 1}.0` : `v${current?.major ?? 1}.${(current?.minor ?? 0) + 1}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Publish this version"
      description="Publishing makes the current draft the official version and locks it against further edits."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="success" loading={publish.isPending} onClick={() => publish.mutate()}>
            Publish {nextLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Version increment">
          <div className="grid grid-cols-2 gap-2">
            {(
              [
                ['minor', 'Minor', 'Clarifications and small corrections'],
                ['major', 'Major', 'Substantive change of obligation'],
              ] as const
            ).map(([value, label, hint]) => (
              <button
                key={value}
                type="button"
                onClick={() => setBump(value)}
                className={`rounded-lg border px-3.5 py-2.5 text-left transition-colors ${
                  bump === value ? 'border-brand-400 bg-brand-50 ring-1 ring-brand-200' : 'border-ink-200 hover:border-ink-300'
                }`}
              >
                <p className="text-[13.5px] font-semibold text-ink-800">{label}</p>
                <p className="mt-0.5 text-[12px] text-ink-500">{hint}</p>
              </button>
            ))}
          </div>
        </Field>

        <Field label="Summary of change" hint="Shown in the version history and the PDF change record.">
          <Input value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="e.g. Added post-incident review trigger" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Effective from" hint="Defaults to today.">
            <Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </Field>
          <Field label="Next review due" hint="Defaults to the organisation's review interval.">
            <Input type="date" value={reviewDue} onChange={(e) => setReviewDue(e.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function ExportModal({ open, onClose, doc }: { open: boolean; onClose: () => void; doc: DocumentDetailDto }) {
  const { push } = useToast();
  const [versionId, setVersionId] = useState(doc.published?.id ?? doc.draft?.id ?? '');
  const [themeId, setThemeId] = useState('');
  const [includeToc, setIncludeToc] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const { data: themes } = useQuery({
    queryKey: ['pdf-themes'],
    queryFn: () => api.get<{ items: PdfThemeDto[] }>('/pdf-themes'),
    enabled: open,
    retry: false,
  });

  const run = async (format: string) => {
    setBusy(format);
    try {
      await downloadFile(`/documents/${doc.id}/export`, {
        format,
        versionId: versionId || undefined,
        themeId: themeId || undefined,
        includeToc: includeToc ? 'true' : 'false',
      });
      push(`Exported as ${format.toUpperCase()}`, 'success');
      onClose();
    } catch (error) {
      push(error instanceof ApiError ? error.message : 'Export failed', 'danger');
    } finally {
      setBusy(null);
    }
  };

  const formats = [
    { id: 'pdf', label: 'PDF', hint: 'Designed, branded, print ready', icon: FileType2 },
    { id: 'markdown', label: 'Markdown', hint: 'Machine readable with front matter', icon: FileCode2 },
    { id: 'docx', label: 'Word', hint: 'For circulation and mark-up', icon: FileType2 },
    { id: 'html', label: 'HTML', hint: 'Self-contained web page', icon: ExternalLink },
    { id: 'json', label: 'JSON', hint: 'Structured content plus metadata', icon: FileJson },
  ];

  return (
    <Modal open={open} onClose={onClose} title="Export document" width="lg">
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Version">
            <Select value={versionId} onChange={(e) => setVersionId(e.target.value)}>
              {doc.versions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label} · {v.publishedAt ? fmtDate(v.publishedAt) : fmtDate(v.createdAt)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="PDF design" hint="Applies to PDF and HTML exports.">
            <Select value={themeId} onChange={(e) => setThemeId(e.target.value)}>
              <option value="">Organisation default</option>
              {themes?.items.map((theme) => (
                <option key={theme.id} value={theme.id}>
                  {theme.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Checkbox label="Include a table of contents" checked={includeToc} onChange={(e) => setIncludeToc(e.target.checked)} />

        <div className="grid gap-2 sm:grid-cols-2">
          {formats.map((format) => (
            <button
              key={format.id}
              type="button"
              disabled={busy !== null}
              onClick={() => run(format.id)}
              className="flex items-center gap-3 rounded-lg border border-ink-200 px-3.5 py-3 text-left transition-colors hover:border-brand-300 hover:bg-brand-50 disabled:opacity-60"
            >
              <format.icon className="size-5 shrink-0 text-brand-600" />
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold text-ink-800">{busy === format.id ? 'Preparing…' : format.label}</p>
                <p className="truncate text-[12px] text-ink-500">{format.hint}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
